import { SearchInput } from "components/Atoms/SearchInput";
import { NoDataAvailableDiv } from "components/NoDataAvailableDiv";
import debounce from "lodash/debounce";
import React, { type Dispatch, type SetStateAction } from "react";
import { type InputOnChangeData } from "semantic-ui-react";
import { formatCurrency } from "utilities";
import ProductGroupDropdown from "../components/dropdowns/reactselect/ProductGroupDropdown";
import { newLineToBr } from "../utilities/newlinetobr";

interface Props {
  productGroups: server.dto.ProductGroup[]
  onTemplateSelected: (template: server.dto.ProductTemplate) => void
  currency: server.dto.Currencies
}

const fetchProductTemplates = async (searchQuery: string, productGroup: server.dto.ProductGroup) => {
  const result = await window.CoreApi.ProductTemplates.Paginated({
    keyword: searchQuery,
    productGroupIds: productGroup?.productGroupId ? [productGroup.productGroupId] : undefined,
    page: 1,
    perPage: 200,
  })
  return result.data.data ?? [];
};

const debouncedSearch = debounce(async (searchQuery: string, productGroup: server.dto.ProductGroup, setTemplates: Dispatch<SetStateAction<server.dto.ProductTemplate[]>>) => {
  const templates = await fetchProductTemplates(searchQuery, productGroup);
  setTemplates(templates);
}, 600);

const getSalesPriceLabel = (template: server.dto.ProductTemplate, currency: server.dto.Currencies) => {
  return !template.salesPricePerUnitExVat ? "" : formatCurrency(template.salesPricePerUnitExVat, currency, serverReference.culture.uiLocale);
};

const Title = ({ template }: { template: server.dto.ProductTemplate }) => {
  const displayTemplateTitle = template.titleForTemplate !== template.title;
  return <>
    <div>
      {template.title}
      {displayTemplateTitle && <span className="s1_gray">&nbsp;({template.titleForTemplate})</span>}
    </div>
    <div className="text-sublabel">
      {template.descriptionForTemplate && newLineToBr(template.descriptionForTemplate)}
    </div>
  </>;
};

const ProductTemplateSelect = (props: Props) => {
  const [templates, setTemplates] = React.useState<server.dto.ProductTemplate[]>([]);
  const [productGroup, setProductGroup] = React.useState<server.dto.ProductGroup | undefined>(undefined);
  const [searchQuery, setSearchQuery] = React.useState("");

  React.useEffect(() => {
    fetchProductTemplates("", undefined).then(templates => {
      setTemplates(templates);
    });
  }, []);

  const onSearchChange = (_, data: InputOnChangeData) => {
    setSearchQuery(data.value);
    debouncedSearch(data.value, productGroup, setTemplates);
  };

  const onProductGroupChange = (newGroup: server.dto.ProductGroup) => {
    setProductGroup(newGroup);
    debouncedSearch(searchQuery, newGroup, setTemplates);
  };

  return <div>

    <div className="margin-bottom">
      <SearchInput
        value={searchQuery}
        onChange={onSearchChange}
        placeholder={RESX.GeneralLabels.Search}
        onKeyDown={e => {
          if (e.key === "Enter") {
            e.preventDefault();
          }
        }}
      />
      {props.productGroups.length > 0 && <ProductGroupDropdown
        options={props.productGroups}
        value={productGroup}
        onChange={onProductGroupChange}
        config={{
          isClearable: true
        }}
      />}
    </div>

    <div>

      {templates.length === 0 && <NoDataAvailableDiv />}

      {templates.length > 0 && <div className="list-with-gaps card-layout">
        <div className="list-with-gaps-body">
          {templates.map(template => (
            <div className="hoverable" key={template.productTemplateId} onClick={() => { props.onTemplateSelected(template); }}>
              <div style={{ flex: "1 1 0" }}><Title template={template} /></div>
              <div style={{ flex: "0 0 150px" }}>{template.productType.text}</div>
              <div style={{ flex: "0 0 150px", textAlign: "right" }}>{getSalesPriceLabel(template, props.currency)}</div>
            </div>
          ))}
        </div>
      </div>}
    </div>
  </div>;
};

export default ProductTemplateSelect;